<template>
    <div class="card">
        <FullCalendar ref="calendarRef" :options="calendarOptions" class="p-0.5" />
        <CalendarEventPreviewCard :open="isPreviewOpen" :model="previewModel" :anchor-rect="previewAnchorRect" :touch-mode="isTouch" @close="closeNow" @card-enter="cancelHide" @card-leave="requestHide" />
    </div>
</template>

<script setup lang="ts">
import type { CalendarPreviewModel } from '@/composables/useCalendarEventPreview';
import { PREVIEW_CARD_ID, useCalendarEventPreview } from '@/composables/useCalendarEventPreview';
import { resourceLocalWallClockDateToMs } from '@/utils/date';
import type { DateSelectArg, EventClickArg, EventDropArg, EventSourceInput } from '@fullcalendar/core';
import allLocales from '@fullcalendar/core/locales-all';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import timeGridPlugin from '@fullcalendar/timegrid';
import FullCalendar from '@fullcalendar/vue3';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

const MAINTENANCE_ACCENT_COLOR = '#D97706';
const DEFAULT_ACCENT_COLOR = '#1b1b7f';

const { isOpen: isPreviewOpen, model: previewModel, anchorRect: previewAnchorRect, isTouch, requestShow, openNow, requestHide, cancelHide, closeNow, blockGesture, unblockGesture, startLongPress, moveLongPress, cancelLongPress } = useCalendarEventPreview();

// D-01/D-08: past-unconfirmed indicator — prepends the warning icon to the title element and labels it for
// assistive technology. The overdue sentence itself now lives inside the hover preview card, so no native title
// attribute is set on the wrapper any more. DOM is built exclusively via createElement + property assignment
// (XSS guard) — reservation titles are user-supplied.
function applyUnconfirmedIndicator(arg: any, titleEl: HTMLElement) {
    if (!arg.event.extendedProps.isPastUnconfirmed) {
        return;
    }
    const iconEl = document.createElement('i');
    iconEl.className = 'fa-solid fa-triangle-exclamation fc-event-unconfirmed-icon';
    // Removing the native title removed the only screen-reader-accessible overdue text on the grid.
    iconEl.setAttribute('role', 'img');
    iconEl.setAttribute('aria-label', t('calendar.unconfirmedTooltip'));
    titleEl.prepend(iconEl);
}

/**
 * Builds the preview card's view-model from an already-rendered FullCalendar event.
 * Everything the card needs is on extendedProps (plan 02-01), so this touches no store and makes no request.
 * Fails closed: a null return means the caller simply does not open a card — there is no loading state to show.
 */
function buildPreviewModel(arg: any): CalendarPreviewModel | null {
    const event = arg?.event;
    const extended = event?.extendedProps;
    if (!event || !extended) {
        return null;
    }

    if (extended.eventType === 'maintenance') {
        // NEVER build the card from the event's own start/end: those are DISPLAY-shifted into the resource's wall
        // clock so FullCalendar draws the event on the right row, and mean nothing as instants. The slot-instant pair
        // read below is the true instant, carried on extendedProps by both event builders for exactly this reason.
        const startMs = extended.slotStartMs;
        const endMs = extended.slotEndMs;
        if (typeof startMs !== 'number' || typeof endMs !== 'number') {
            return null;
        }

        return {
            eventType: 'maintenance',
            title: event.title ?? '',
            startMs,
            endMs,
            resourceName: extended.resourceName ?? '',
            accentColor: MAINTENANCE_ACCENT_COLOR,
            otherResourceCount: 0,
            status: null,
            isPastUnconfirmed: false,
            visitorName: null,
            ownerName: null,
            attendees: 0,
            reducedCapacity: extended.reducedCapacity ?? null,
            resourceTimezoneId: extended.resourceTimezoneId ?? null,
            description: extended.description ?? ''
        };
    }

    // D-05: the hovered slot's own range, not the whole reservation's.
    if (typeof extended.slotStartMs !== 'number' || typeof extended.slotEndMs !== 'number') {
        return null;
    }

    return {
        eventType: 'reservation',
        title: event.title ?? '',
        startMs: extended.slotStartMs,
        endMs: extended.slotEndMs,
        resourceName: extended.resourceName ?? '',
        accentColor: extended.accentColor ?? DEFAULT_ACCENT_COLOR,
        otherResourceCount: extended.otherResourceCount ?? 0,
        status: extended.status ?? null,
        isPastUnconfirmed: extended.isPastUnconfirmed === true,
        visitorName: extended.visitorName ?? null,
        ownerName: extended.ownerName ?? null,
        attendees: extended.attendees ?? 0,
        // Reservation events carry no cap — only a maintenance does.
        reducedCapacity: null,
        resourceTimezoneId: extended.resourceTimezoneId ?? null,
        description: extended.description ?? ''
    };
}

interface Props {
    eventSource: EventSourceInput;
}

const props = defineProps<Props>();

const emit = defineEmits<{
    eventClick: [reservationId: string, resourceId: string];
    eventDrop: [reservationId: string, resourceId: string, newStart: number, newEnd: number, revert: () => void];
    eventResize: [reservationId: string, resourceId: string, newStart: number, newEnd: number, revert: () => void];
    selectSlot: [startMs: number, endMs: number];
}>();

const calendarRef = ref<InstanceType<typeof FullCalendar> | null>(null);

const MOBILE_BREAKPOINT = 768;
const windowWidth = ref(typeof window !== 'undefined' ? window.innerWidth : 1024);

const initialView = computed(() => {
    return windowWidth.value < MOBILE_BREAKPOINT ? 'listWeek' : 'dayGridMonth';
});

function handleResize() {
    windowWidth.value = window.innerWidth;

    const calendarApi = calendarRef.value?.getApi();
    if (calendarApi) {
        const currentView = calendarApi.view.type;
        const isMobile = windowWidth.value < MOBILE_BREAKPOINT;

        if (isMobile) {
            calendarApi.setOption('headerToolbar', {
                left: 'title',
                center: '',
                right: ''
            });
            calendarApi.setOption('footerToolbar', {
                left: 'prev,next today',
                center: 'dayGridMonth,listWeek,timeGridDay',
                right: ''
            });
        } else {
            calendarApi.setOption('headerToolbar', {
                left: 'title',
                center: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                right: 'prev,next today'
            });
            calendarApi.setOption('footerToolbar', undefined);
        }

        if (isMobile && currentView === 'timeGridWeek') {
            calendarApi.changeView('listWeek');
        } else if (!isMobile && currentView === 'listWeek') {
            calendarApi.changeView('timeGridWeek');
        }
    }
}

onMounted(() => {
    window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
    window.removeEventListener('resize', handleResize);
});

function onEventClick(arg: EventClickArg) {
    const eventType = arg.event.extendedProps.eventType as string;

    if (eventType === 'maintenance') {
        return;
    }

    // The detail modal is opening — the card must be gone before it does.
    closeNow();

    const popover = document.querySelector('.fc-more-popover');
    if (popover) {
        popover.remove();
    }

    const { reservationId, resourceId } = arg.event.extendedProps as {
        reservationId: string;
        resourceId: string;
    };

    emit('eventClick', reservationId, resourceId);
}

function onEventDrop(info: EventDropArg) {
    if (info.event.extendedProps.eventType === 'maintenance') {
        info.revert();
        return;
    }

    const { reservationId, resourceId } = info.event.extendedProps as {
        reservationId: string;
        resourceId: string;
    };

    // THE UN-SHIFT. What FullCalendar hands back after the gesture is in the DISPLAY frame: the operator dropped the
    // event on a row that means a wall clock in the SPACE's zone. What leaves this function must be a TRUE instant —
    // the consumer pairs it against slot.startMs (a true instant) in the change modal and sends it to the backend.
    const zoneId = info.event.extendedProps.resourceTimezoneId ?? null;
    const newStart = resourceLocalWallClockDateToMs(zoneId, info.event.start) ?? 0;
    const newEnd = resourceLocalWallClockDateToMs(zoneId, info.event.end) ?? 0;

    emit('eventDrop', reservationId, resourceId, newStart, newEnd, info.revert);
}

function onEventResize(info: any) {
    if (info.event.extendedProps.eventType === 'maintenance') {
        info.revert();
        return;
    }

    const { reservationId, resourceId } = info.event.extendedProps as {
        reservationId: string;
        resourceId: string;
    };

    // THE UN-SHIFT, same invariant as onEventDrop above: a resized end is a wall clock in the SPACE's zone, and what
    // leaves this function must be a TRUE instant because the consumer compares it to slot.endMs and persists it.
    const zoneId = info.event.extendedProps.resourceTimezoneId ?? null;
    const newStart = resourceLocalWallClockDateToMs(zoneId, info.event.start) ?? 0;
    const newEnd = resourceLocalWallClockDateToMs(zoneId, info.event.end) ?? 0;

    emit('eventResize', reservationId, resourceId, newStart, newEnd, info.revert);
}

function onSelectSlot(info: DateSelectArg) {
    // Fast-path release for the drag-select gestures FullCalendar actually reports.
    unblockGesture();
    emit('selectSlot', info.start.getTime(), info.end.getTime());
}

// Browsers replay a touch as mouseover/mousemove shortly after touchend. On a hybrid device (a touchscreen
// laptop driven by a mouse) isTouch is false, so without this guard that replay would open a card through the
// hover path and leave it stuck until a tap elsewhere synthesised a mouseout -- the classic sticky-hover
// artefact the touch branch exists to avoid. Inert on mouse-only hardware, where no touch event ever fires.
const SYNTHETIC_MOUSE_WINDOW_MS = 700;
let lastTouchAt = 0;
let touchActive = false;

function isTouchOriginated(): boolean {
    return touchActive || Date.now() - lastTouchAt < SYNTHETIC_MOUSE_WINDOW_MS;
}

function onEventMouseEnter(arg: any) {
    // Touch is handled by the long-press path; synthetic mouse events would double-fire here.
    if (isTouch.value || isTouchOriginated()) {
        return;
    }

    // D-09: desktop list rows already print title, time and space, so the card would only repeat what is on screen.
    // The gate is view-type based, so touch listWeek (D-12) is unaffected — it never reaches this handler.
    if (arg.view?.type === 'listWeek') {
        return;
    }

    const model = buildPreviewModel(arg);
    if (model === null) {
        return;
    }

    // Events inside the month-view "+N more" popover fire this handler normally (D-10); stacking is handled by z-index.
    requestShow(model, arg.el);
    setPreviewDescribedBy(arg.el);
}

function onEventMouseLeave() {
    setPreviewDescribedBy(null);
    requestHide();
}

// role="tooltip" on the card is inert on its own: assistive technology reaches a tooltip through the element it
// describes. Point the anchor at the card while it is open and drop the attribute afterwards, so the DOM never
// keeps a dangling reference to an id that is no longer rendered.
let describedAnchorEl: HTMLElement | null = null;

function setPreviewDescribedBy(el: HTMLElement | null) {
    if (describedAnchorEl !== null && describedAnchorEl !== el) {
        describedAnchorEl.removeAttribute('aria-describedby');
    }
    describedAnchorEl = el;
    if (el !== null) {
        el.setAttribute('aria-describedby', PREVIEW_CARD_ID);
    }
}

// Covers every close the anchor handlers do not see: the touch close button, Escape, scroll and view change.
watch(isPreviewOpen, (open) => {
    if (!open) {
        setPreviewDescribedBy(null);
    }
});

// View change and date navigation both invalidate the pinned anchor rect. Clearing the gesture block here as well
// means a stale block cannot survive a view switch even hypothetically.
function onDatesSet() {
    closeNow();
    unblockGesture();
}

interface EventTouchHandlers {
    onTouchStart: (event: TouchEvent) => void;
    onTouchMove: (event: TouchEvent) => void;
    onTouchEnd: (event: TouchEvent) => void;
    onTouchCancel: () => void;
    onContextMenu: (event: Event) => void;
    onFocus: () => void;
    onBlur: () => void;
}

// FullCalendar re-creates event elements aggressively on view and date change, so every listener is keyed to the
// element it was attached to and dropped in eventWillUnmount. A WeakMap also lets a discarded element be collected.
const eventTouchHandlers = new WeakMap<HTMLElement, EventTouchHandlers>();

/**
 * D-11/D-12: touch access to the preview card. Long-press opens it on any event; a plain tap opens it on a
 * maintenance only, because a maintenance has no click action at all and would otherwise be unreachable.
 * A tap on a visit is left alone and falls through to eventClick, which still opens the detail modal.
 *
 * Attached unconditionally rather than behind an isTouch gate. A touchscreen laptop driven by a mouse reports
 * neither '(hover: none)' nor '(pointer: coarse)', so gating here left those devices with no touch path at all;
 * and because nothing re-ran eventDidMount when the media query flipped mid-session, already-mounted events
 * stayed wired to whichever mode was current at mount. Both problems disappear when attachment does not depend
 * on the mode: the listeners are inert on hardware that never dispatches a touch event.
 *
 * Note for behavioural verification: FullCalendar starts its own touch drag at `longPressDelay` (1000ms default),
 * which fires eventDragStart and therefore closes the card. Holding an editable event for over a second is a drag,
 * not a preview; the card appears at 500ms, well before that point.
 */
function onEventDidMount(arg: any) {
    const el = arg?.el as HTMLElement | undefined;
    if (!el) {
        return;
    }

    const isMaintenance = arg.event?.extendedProps?.eventType === 'maintenance';

    const onTouchStart = (event: TouchEvent) => {
        touchActive = true;
        lastTouchAt = Date.now();
        const touch = event.touches[0];
        if (!touch) {
            return;
        }
        const model = buildPreviewModel(arg);
        if (model === null) {
            return;
        }
        startLongPress(model, el, { x: touch.clientX, y: touch.clientY });
    };

    const onTouchMove = (event: TouchEvent) => {
        const touch = event.touches[0];
        if (!touch) {
            return;
        }
        // Cancels the pending press past TOUCH_MOVE_CANCEL_PX, so the gesture never hijacks a scroll or a drag.
        moveLongPress({ x: touch.clientX, y: touch.clientY });
    };

    const onTouchEnd = (event: TouchEvent) => {
        touchActive = false;
        lastTouchAt = Date.now();
        const outcome = cancelLongPress();

        if (outcome === 'fired') {
            // Suppress the synthetic click so the detail modal cannot open behind the card.
            event.preventDefault();
            return;
        }

        // A finger that travelled was scrolling the calendar, not tapping this event. Opening a card here is
        // what made every swipe starting on a maintenance pop one open on lift.
        if (outcome === 'cancelled' || !isMaintenance) {
            return;
        }

        const model = buildPreviewModel(arg);
        if (model === null) {
            return;
        }
        openNow(model, el);
        setPreviewDescribedBy(el);
    };

    const onTouchCancel = () => {
        touchActive = false;
        lastTouchAt = Date.now();
        cancelLongPress();
    };

    const onContextMenu = (event: Event) => {
        // The OS long-press menu must never compete with the card. Suppressed only for a touch-originated
        // menu: now that this listener is attached on every device, blanket preventDefault would swallow the
        // native right-click menu on the desktop, which was never in scope.
        if (!isTouchOriginated()) {
            return;
        }
        event.preventDefault();
    };

    // Keyboard parity for the hover path (WR-07): without this, the visitor, owner, attendee count, other-resource
    // count and notes excerpt are mouse-exclusive. No tabIndex is forced — FullCalendar already applies
    // tabindex="0" to interactive event segments whenever an eventClick handler exists, so the tab order is
    // unchanged and only events the operator can already reach gain a card. Opens immediately: focus is a
    // discrete deliberate move, with none of the sweeping a hover delay exists to debounce.
    const onFocus = () => {
        if (arg.view?.type === 'listWeek') {
            return;
        }
        const model = buildPreviewModel(arg);
        if (model === null) {
            return;
        }
        openNow(model, el);
        setPreviewDescribedBy(el);
    };

    const onBlur = () => {
        setPreviewDescribedBy(null);
        requestHide();
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: true });
    el.addEventListener('touchend', onTouchEnd);
    el.addEventListener('touchcancel', onTouchCancel, { passive: true });
    el.addEventListener('contextmenu', onContextMenu);
    el.addEventListener('focus', onFocus);
    el.addEventListener('blur', onBlur);

    eventTouchHandlers.set(el, { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel, onContextMenu, onFocus, onBlur });
}

function onEventWillUnmount(arg: any) {
    const el = arg?.el as HTMLElement | undefined;
    if (!el) {
        return;
    }

    // FullCalendar re-creates elements aggressively, so an element carrying aria-describedby can be discarded
    // while the card is still open. Drop the reference with it rather than leaving a stale one behind.
    if (describedAnchorEl === el) {
        setPreviewDescribedBy(null);
    }

    const handlers = eventTouchHandlers.get(el);
    if (!handlers) {
        return;
    }

    el.removeEventListener('touchstart', handlers.onTouchStart);
    el.removeEventListener('touchmove', handlers.onTouchMove);
    el.removeEventListener('touchend', handlers.onTouchEnd);
    el.removeEventListener('touchcancel', handlers.onTouchCancel);
    el.removeEventListener('contextmenu', handlers.onContextMenu);
    el.removeEventListener('focus', handlers.onFocus);
    el.removeEventListener('blur', handlers.onBlur);
    eventTouchHandlers.delete(el);
}

const calendarOptions = ref({
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    headerToolbar:
        windowWidth.value < MOBILE_BREAKPOINT
            ? {
                left: 'title',
                center: '',
                right: ''
            }
            : {
                left: 'title',
                center: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                right: 'prev,next today'
            },
    footerToolbar:
        windowWidth.value < MOBILE_BREAKPOINT
            ? {
                left: 'prev,next today',
                center: 'dayGridMonth,listWeek,timeGridDay',
                right: ''
            }
            : undefined,
    initialView: initialView.value,
    allDaySlot: false,
    height: 'calc(100vh - 12rem)',
    eventDisplay: 'block',
    editable: true,
    selectable: true,
    selectMirror: true,
    dayMaxEvents: 2,
    views: {
        dayGridMonth: {
            dayMaxEvents: 3
        }
    },
    weekends: true,
    locales: allLocales,
    firstDay: 1,
    events: props.eventSource,
    locale: locale.value.split('-')[0],
    nowIndicator: true,
    scrollTime: '08:00:00',
    slotDuration: '00:15:00',
    slotLabelInterval: '00:30:00',
    snapDuration: '00:15:00',
    slotEventOverlap: false,
    selectAllow: (selectInfo: any) => {
        // D-04: the only signal FullCalendar gives at the START of a range drag. It fires on every mousemove of the
        // gesture, which is fine — blockGesture()/unblockGesture() are idempotent. The block is released by the
        // composable's own window pointerup/pointercancel listener, NOT by the handlers below: a cross-day drag makes
        // this callback return false, after which FullCalendar fires neither select nor unselect, and a callback-only
        // release would latch the block for the rest of the session.
        blockGesture();
        const start = selectInfo.start;
        const end = selectInfo.end;
        // Allow only same-day selections
        return start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth() && start.getDate() === end.getDate();
    },
    eventMaxStack: 2,
    eventOrder: (a: any, b: any) => {
        // Always sort by start time first (ascending)
        const aStart = a.start?.getTime?.() ?? 0;
        const bStart = b.start?.getTime?.() ?? 0;
        if (aStart !== bStart) {
            return aStart - bStart;
        }
        // If same start, put maintenance after reservations
        const aType = a.extendedProps?.eventType === 'maintenance' ? 1 : 0;
        const bType = b.extendedProps?.eventType === 'maintenance' ? 1 : 0;
        return aType - bType;
    },
    slotLabelFormat: {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    },
    eventClick: onEventClick,
    eventDrop: onEventDrop,
    eventResize: onEventResize,
    select: onSelectSlot,
    unselect: unblockGesture,
    eventMouseEnter: onEventMouseEnter,
    eventMouseLeave: onEventMouseLeave,
    eventDragStart: blockGesture,
    eventDragStop: unblockGesture,
    eventResizeStart: blockGesture,
    eventResizeStop: unblockGesture,
    datesSet: onDatesSet,
    eventDidMount: onEventDidMount,
    eventWillUnmount: onEventWillUnmount,
    eventContent: (arg: any) => {
        const { resourceName, description, status, accentColor, eventType } = arg.event.extendedProps;

        const start = arg.event.start;
        const end = arg.event.end;
        const isMonthView = arg.view?.type === 'dayGridMonth';
        const durationMs = end - start;
        const durationMinutes = Math.floor(durationMs / (1000 * 60));
        const hours = Math.floor(durationMinutes / 60);
        const minutes = durationMinutes % 60;

        let durationText = '';
        if (hours > 0 && minutes > 0) {
            durationText = `${hours}h ${minutes}m`;
        } else if (hours > 0) {
            durationText = `${hours}h`;
        } else {
            durationText = `${minutes}m`;
        }

        const startTimeText = start ? `${String(start.getHours()).padStart(2, '0')}:${String(start.getMinutes()).padStart(2, '0')}` : '';
        const endTimeText = end ? `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}` : '';
        const slotRangeText = startTimeText && endTimeText ? `${startTimeText} - ${endTimeText}` : startTimeText;

        // Outer wrapper
        const wrapper = document.createElement('div');
        wrapper.className = 'fc-event-wrapper';

        // Status bar
        const bar = document.createElement('div');
        bar.className = 'fc-event-status-bar';

        if (eventType === 'maintenance') {
            bar.style.backgroundColor = '#D97706';
        } else {
            const color = accentColor || '#1b1b7f';
            switch (status) {
                case 2: // Completed
                    bar.classList.add('status-completed');
                    bar.style.setProperty('--accent-color', color);
                    break;
                case 0: // Planned
                    bar.classList.add('status-planned');
                    bar.style.setProperty('--accent-color', color);
                    break;
                case 1: // Canceled
                    bar.classList.add('status-canceled');
                    bar.style.setProperty('--accent-color', color);
                    break;
                default:
                    bar.style.backgroundColor = color;
            }
        }

        wrapper.appendChild(bar);

        // Content area
        const content = document.createElement('div');
        content.className = 'fc-event-body';

        if (durationMinutes <= 30) {
            content.classList.add('fc-event-compact');

            const titleEl = document.createElement('span');
            titleEl.className = 'fc-event-custom-title';
            titleEl.textContent = arg.event.title;
            applyUnconfirmedIndicator(arg, titleEl);
            content.appendChild(titleEl);

            const timeEl = document.createElement('span');
            timeEl.className = 'fc-event-custom-time';
            timeEl.textContent = isMonthView ? slotRangeText : startTimeText;
            content.appendChild(timeEl);
        } else if (durationMinutes <= 45) {
            const titleEl = document.createElement('div');
            titleEl.className = 'fc-event-custom-title';
            titleEl.textContent = arg.event.title;
            applyUnconfirmedIndicator(arg, titleEl);
            content.appendChild(titleEl);

            const metaRow = document.createElement('div');
            metaRow.className = 'fc-event-meta';

            if (resourceName) {
                const resEl = document.createElement('span');
                resEl.className = 'fc-event-custom-resource';
                resEl.textContent = resourceName;
                metaRow.appendChild(resEl);
            }

            const durEl = document.createElement('span');
            durEl.className = 'fc-event-custom-duration';
            durEl.textContent = isMonthView ? slotRangeText : durationText;
            metaRow.appendChild(durEl);

            content.appendChild(metaRow);
        } else {
            const titleEl = document.createElement('div');
            titleEl.className = 'fc-event-custom-title';
            titleEl.textContent = arg.event.title;
            applyUnconfirmedIndicator(arg, titleEl);
            content.appendChild(titleEl);

            if (description) {
                const descEl = document.createElement('div');
                descEl.className = 'fc-event-custom-description';
                descEl.textContent = description;
                content.appendChild(descEl);
            }

            const metaRow = document.createElement('div');
            metaRow.className = 'fc-event-meta';

            if (resourceName) {
                const resEl = document.createElement('span');
                resEl.className = 'fc-event-custom-resource';
                resEl.textContent = resourceName;
                metaRow.appendChild(resEl);
            }

            const durEl = document.createElement('span');
            durEl.className = 'fc-event-custom-duration';
            durEl.textContent = isMonthView ? slotRangeText : durationText;
            metaRow.appendChild(durEl);

            content.appendChild(metaRow);
        }

        wrapper.appendChild(content);
        return { domNodes: [wrapper] };
    }
});

function refetchEvents() {
    const calendarApi = calendarRef.value?.getApi();
    if (calendarApi) {
        calendarApi.refetchEvents();
    }
}

defineExpose({
    refetchEvents
});

watch(
    locale,
    (newLocale) => {
        const calendarLocale = newLocale.split('-')[0];
        calendarOptions.value.locale = calendarLocale;
    },
    { immediate: true }
);
</script>

<style scoped>
/* Only layout/responsive styles that are specific to this component */
.card {
    overflow-x: auto;
}

:deep(.fc) {
    min-width: 800px;
}

:deep(.fc-col-header-cell),
:deep(.fc-daygrid-day),
:deep(.fc-timegrid-col) {
    min-width: 200px !important;
}

@media (max-width: 768px) {
    .card {
        overflow-x: visible;
    }

    :deep(.fc) {
        min-width: unset;
    }

    :deep(.fc-col-header-cell),
    :deep(.fc-daygrid-day),
    :deep(.fc-timegrid-col) {
        min-width: unset !important;
    }

    :deep(.fc-toolbar-title) {
        font-size: 1.2rem !important;
        line-height: 1.3;
    }

    :deep(.fc-toolbar) {
        flex-wrap: wrap;
        gap: 0.5rem;
    }

    :deep(.fc-toolbar-chunk) {
        width: 100%;
    }

    :deep(.fc-button) {
        padding: 0.3rem 0.6rem;
        font-size: 0.85rem;
    }

    :deep(.fc-footer-toolbar) {
        justify-content: center;
        text-align: center;
    }
}
</style>
